import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import { supabase } from "@/lib/supabase";
import { useUser } from "@/contexts/UserContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ============================
   Trasporto autenticato (OAuth/JWT/Cookie)
   ============================ */
function useAuthTransport() {
  const [accessToken, setAccessToken] = useState(null);

  useEffect(() => {
    (async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (session?.access_token) setAccessToken(session.access_token);
    })();
  }, []);

  const cfg = useMemo(() => {
    if (accessToken) {
      return {
        headers: { Authorization: `Bearer ${accessToken}` },
        withCredentials: false,
        accessToken,
      };
    }
    // No Supabase token — fall back to axios default headers (Authorization or X-User-Id
    // set by Login.js for email/password flow).
    const fallbackHeaders = {};
    const defaultAuth = axios.defaults.headers?.common?.Authorization;
    const defaultUserId = axios.defaults.headers?.common?.['X-User-Id']
      || axios.defaults.headers?.common?.['x-user-id'];
    if (defaultAuth) {
      fallbackHeaders.Authorization = defaultAuth;
    }
    if (defaultUserId) {
      fallbackHeaders['X-User-Id'] = defaultUserId;
    }
    return {
      headers: fallbackHeaders,
      withCredentials: axios.defaults.withCredentials === true,
      accessToken: null,
    };
  }, [accessToken]);

  return cfg;
}

/* ============================
   Componente Onboarding
   ============================ */
const Onboarding = ({ onComplete }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { fetchUserData } = useUser();

  const [currentStep, setCurrentStep] = useState(0);
  const storedGoogleName = localStorage.getItem("betterhusbandUserName") || "";
  const storedGooglePicture =
    localStorage.getItem("betterhusbandGooglePicture") ||
    localStorage.getItem("betterhusbandGoglePicture") ||
    "";

  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: storedGoogleName,
    birth_date: "",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Rome",
    language: i18n.language || "en",
    tone_preference: "",
    reminder_time: "19:00",
    relationship_type: "",
    relationship_duration_months: null,
    has_children: null,
    children: [],
    communication_level: "",
    stress_level: "",
    intimacy_level: "",
    daily_availability_minutes: 10,
    work_schedule: "",
    time_together: "",
    household_management: "",
    tension_points: [],
    partner_name: "",
    acceptedTerms: false,
    acceptedPrivacy: false,
    picture: storedGooglePicture,
  });

  const totalSteps = 8;

  const isStepValid = () => {
    switch (currentStep) {
      case 0:
        return formData.name.trim() !== "" && formData.birth_date !== "" && formData.language !== "";
      case 1:
        return (
          formData.relationship_type !== "" &&
          formData.relationship_duration_months !== null &&
          formData.has_children !== null &&
          (formData.has_children === false ||
            (formData.children.length > 0 &&
              formData.children.every((c) => c.age !== null)))
        );
      case 2:
        return (
          formData.communication_level !== "" &&
          formData.stress_level !== "" &&
          formData.intimacy_level !== ""
        );
      case 3:
        return (
          formData.work_schedule !== "" &&
          formData.time_together !== "" &&
          formData.household_management !== ""
        );
      case 4:
        return formData.tension_points.length >= 1;
      case 5:
        return formData.tone_preference !== "";
      case 6:
        return formData.partner_name.trim() !== "";
      case 7:
        return formData.acceptedTerms && formData.acceptedPrivacy;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep((s) => s + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep((s) => s - 1);
    }
  };

  const updateFormData = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const addChild = () => {
    setFormData((prev) => ({
      ...prev,
      children: [...prev.children, { id: Date.now(), age: null }],
    }));
  };

  const removeChild = (id) => {
    setFormData((prev) => ({
      ...prev,
      children: prev.children.filter((c) => c.id !== id),
    }));
  };

  const updateChildAge = (id, age) => {
    setFormData((prev) => ({
      ...prev,
      children: prev.children.map((c) => (c.id === id ? { ...c, age } : c)),
    }));
  };

  const authCfg = useAuthTransport();

  const handleSubmit = async () => {
    if (saving) return;
    setSaving(true);

    try {
      const submitData = {
        ...formData,
        number_of_children: formData.children.length,
        children_ages: formData.children.map((c) => c.age),
      };

      // Salva onboarding tramite API
      await axios.patch(`${API}/users/me`, submitData, {
        headers: {
          ...(authCfg.headers || {}),
          "Content-Type": "application/json",
        },
        withCredentials: authCfg.withCredentials,
      });

      // Ricarica profilo per conferma
      const profileResp = await axios.get(`${API}/users/me`, {
        headers: authCfg.headers || {},
        withCredentials: authCfg.withCredentials,
      });

      const fullProfile = profileResp.data;
      const effectiveUserId = fullProfile.id || fullProfile.user_id;

      // Salva in localStorage
      localStorage.setItem("betterhusbandUser", JSON.stringify(fullProfile));
      if (effectiveUserId) {
        localStorage.setItem("betterhusbandUserId", effectiveUserId);
      }

      // Aggiorna UserContext con il profilo completo (include picture, name, email, etc.)
      fetchUserData();

      // Lancia confetti
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });

      toast.success(t("onboarding.completed") || "Onboarding completato!");

      // Notifica App.js del completamento
      onComplete?.(effectiveUserId);
      navigate("/dashboard", { replace: true });
    } catch (error) {
      console.error("Onboarding error:", error);
      const msg =
        error?.response?.data?.detail ||
        error?.message ||
        "Errore durante il salvataggio";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  /* ============================
     UI: renderStep
     ============================ */
  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">
                {t("onboarding.step1.name")}
              </label>
              <input
                data-testid="onboarding-name-input"
                type="text"
                value={formData.name}
                onChange={(e) => updateFormData("name", e.target.value)}
                placeholder={t("onboarding.step1.namePlaceholder")}
                className="w-full h-12 bg-white border border-stone-200 rounded-full px-4 text-stone-900 placeholder:text-stone-400 focus:border-stone-900 focus:ring-0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">
                {t("onboarding.step1.birthDate")}
              </label>
              <input
                data-testid="onboarding-birthdate-input"
                type="date"
                value={formData.birth_date}
                onChange={(e) => updateFormData("birth_date", e.target.value)}
                max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                min={new Date(new Date().setFullYear(new Date().getFullYear() - 100)).toISOString().split('T')[0]}
                className="w-full h-12 bg-white border border-stone-200 rounded-full px-4 text-stone-900 focus:border-stone-900 focus:ring-0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">
                {t("onboarding.step1.timezone")}
              </label>
              <select
                data-testid="onboarding-timezone-select"
                value={formData.timezone}
                onChange={(e) => updateFormData("timezone", e.target.value)}
                className="w-full h-12 bg-white border border-stone-200 rounded-full px-4 text-stone-900 focus:border-stone-900 focus:ring-0"
              >
                <option value="Europe/Rome">🇮🇹 Italia (Roma)</option>
                <option value="Europe/London">🇬🇧 UK (Londra)</option>
                <option value="Europe/Paris">🇫🇷 Francia (Parigi)</option>
                <option value="Europe/Berlin">🇩🇪 Germania (Berlino)</option>
                <option value="Europe/Madrid">🇪🇸 Spagna (Madrid)</option>
                <option value="Europe/Zurich">🇨🇭 Svizzera (Zurigo)</option>
                <option value="America/New_York">🇺🇸 USA Est (New York)</option>
                <option value="America/Los_Angeles">🇺🇸 USA Ovest (Los Angeles)</option>
                <option value="America/Chicago">🇺🇸 USA Centro (Chicago)</option>
                <option value="America/Sao_Paulo">🇧🇷 Brasile (São Paulo)</option>
                <option value="Australia/Sydney">🇦🇺 Australia (Sydney)</option>
                <option value="Asia/Tokyo">🇯🇵 Giappone (Tokyo)</option>
                <option value="Asia/Dubai">🇦🇪 Dubai</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">
                {t("onboarding.step1.language")}
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  data-testid="onboarding-lang-it-btn"
                  onClick={() => {
                    updateFormData("language", "it");
                    i18n.changeLanguage("it");
                  }}
                  className={`h-12 rounded-full border transition-all ${
                    formData.language === "it"
                      ? "bg-stone-900 text-white border-stone-900"
                      : "bg-white text-stone-700 border-stone-200 hover:border-stone-400"
                  }`}
                >
                  {t("onboarding.step1.italian")}
                </button>
                <button
                  data-testid="onboarding-lang-en-btn"
                  onClick={() => {
                    updateFormData("language", "en");
                    i18n.changeLanguage("en");
                  }}
                  className={`h-12 rounded-full border transition-all ${
                    formData.language === "en"
                      ? "bg-stone-900 text-white border-stone-900"
                      : "bg-white text-stone-700 border-stone-200 hover:border-stone-400"
                  }`}
                >
                  {t("onboarding.step1.english")}
                </button>
              </div>
            </div>
          </div>
        );

      case 1:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">
                {t("onboarding.step2.type")}
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  data-testid="onboarding-type-cohabitating-btn"
                  onClick={() =>
                    updateFormData("relationship_type", "cohabitating")
                  }
                  className={`h-12 rounded-full border transition-all ${
                    formData.relationship_type === "cohabitating"
                      ? "bg-stone-900 text-white border-stone-900"
                      : "bg-white text-stone-700 border-stone-200 hover:border-stone-400"
                  }`}
                >
                  {t("onboarding.step2.cohabitating")}
                </button>
                <button
                  data-testid="onboarding-type-married-btn"
                  onClick={() => updateFormData("relationship_type", "married")}
                  className={`h-12 rounded-full border transition-all ${
                    formData.relationship_type === "married"
                      ? "bg-stone-900 text-white border-stone-900"
                      : "bg-white text-stone-700 border-stone-200 hover:border-stone-400"
                  }`}
                >
                  {t("onboarding.step2.married")}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">
                {t("onboarding.step2.duration")}
              </label>
              <select
                data-testid="onboarding-duration-select"
                value={formData.relationship_duration_months || ""}
                onChange={(e) =>
                  updateFormData(
                    "relationship_duration_months",
                    parseInt(e.target.value, 10)
                  )
                }
                className="w-full h-12 bg-white border border-stone-200 rounded-full px-4 text-stone-900 focus:border-stone-900 focus:ring-0"
              >
                <option value="" disabled>
                  Seleziona...
                </option>
                <option value="6">{t("onboarding.step2.durationLessThan1")}</option>
                <option value="18">{t("onboarding.step2.duration1to2")}</option>
                <option value="42">{t("onboarding.step2.duration2to5")}</option>
                <option value="90">{t("onboarding.step2.duration5to10")}</option>
                <option value="144">{t("onboarding.step2.duration10plus")}</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-3">
                {t("onboarding.step2.children")}
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  data-testid="onboarding-children-no-btn"
                  onClick={() => {
                    updateFormData("has_children", false);
                    setFormData((prev) => ({ ...prev, children: [] }));
                  }}
                  className={`h-12 rounded-full border transition-all ${
                    formData.has_children === false
                      ? "bg-stone-900 text-white border-stone-900"
                      : "bg-white text-stone-700 border-stone-200 hover:border-stone-400"
                  }`}
                >
                  {t("onboarding.step2.no")}
                </button>
                <button
                  data-testid="onboarding-children-yes-btn"
                  onClick={() => {
                    updateFormData("has_children", true);
                    if (formData.children.length === 0) {
                      addChild();
                    }
                  }}
                  className={`h-12 rounded-full border transition-all ${
                    formData.has_children === true
                      ? "bg-stone-900 text-white border-stone-900"
                      : "bg-white text-stone-700 border-stone-200 hover:border-stone-400"
                  }`}
                >
                  {t("onboarding.step2.yes")}
                </button>
              </div>
            </div>

            {formData.has_children === true && (
              <div className="space-y-3">
                {formData.children.map((child, index) => (
                  <div
                    key={child.id}
                    className="bg-stone-50 border border-stone-200 rounded-2xl p-4 space-y-3"
                  >
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium text-stone-700">
                        {t("onboarding.step2.childAge")} {index + 1}
                      </label>
                      {formData.children.length > 1 && (
                        <button
                          data-testid={`remove-child-${index}-btn`}
                          onClick={() => removeChild(child.id)}
                          className="text-xs text-red-600 hover:text-red-700"
                        >
                          {t("onboarding.step2.removeChild")}
                        </button>
                      )}
                    </div>
                    <select
                      data-testid={`child-age-${index}-select`}
                      value={child.age || ""}
                      onChange={(e) =>
                        updateChildAge(child.id, parseInt(e.target.value, 10))
                      }
                      className="w-full h-10 bg-white border border-stone-200 rounded-full px-3 text-sm text-stone-900 focus:border-stone-900 focus:ring-0"
                    >
                      <option value="" disabled>
                        Seleziona età...
                      </option>
                      <option value="0">{t("onboarding.step2.ageLessThan1")}</option>
                      <option value="1">{t("onboarding.step2.age1")}</option>
                      <option value="2">{t("onboarding.step2.age2")}</option>
                      <option value="3">{t("onboarding.step2.age3")}</option>
                      <option value="4">{t("onboarding.step2.age4")}</option>
                      <option value="5">{t("onboarding.step2.age5")}</option>
                      <option value="6">{t("onboarding.step2.age6")}</option>
                      <option value="7">{t("onboarding.step2.age7")}</option>
                      <option value="8">{t("onboarding.step2.age8")}</option>
                      <option value="9">{t("onboarding.step2.age9")}</option>
                      <option value="10">{t("onboarding.step2.age10")}</option>
                      <option value="11">{t("onboarding.step2.age11")}</option>
                      <option value="12">{t("onboarding.step2.age12")}</option>
                      <option value="13">{t("onboarding.step2.age13")}</option>
                      <option value="14">{t("onboarding.step2.age14")}</option>
                      <option value="15">{t("onboarding.step2.age15")}</option>
                      <option value="16">{t("onboarding.step2.age16")}</option>
                      <option value="17">{t("onboarding.step2.age17")}</option>
                      <option value="18">{t("onboarding.step2.age18plus")}</option>
                    </select>
                  </div>
                ))}
                <button
                  data-testid="add-child-btn"
                  onClick={addChild}
                  className="w-full h-10 rounded-full border-2 border-dashed border-stone-300 text-stone-600 hover:border-stone-400 hover:text-stone-700 transition-all text-sm font-medium"
                >
                  + {t("onboarding.step2.addChild")}
                </button>
              </div>
            )}
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-3">
                {t("onboarding.step3.communication")}
              </label>
              <div className="space-y-2">
                {["low", "medium", "high"].map((level) => (
                  <button
                    key={level}
                    data-testid={`onboarding-comm-${level}-btn`}
                    onClick={() => updateFormData("communication_level", level)}
                    className={`w-full h-12 rounded-full border transition-all text-left px-4 ${
                      formData.communication_level === level
                        ? "bg-stone-900 text-white border-stone-900"
                        : "bg-white text-stone-700 border-stone-200 hover:border-stone-400"
                    }`}
                  >
                    {t(
                      `onboarding.step3.comm${
                        level.charAt(0).toUpperCase() + level.slice(1)
                      }`
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-3">
                {t("onboarding.step3.stress")}
              </label>
              <div className="space-y-2">
                {["low", "medium", "high"].map((level) => (
                  <button
                    key={level}
                    data-testid={`onboarding-stress-${level}-btn`}
                    onClick={() => updateFormData("stress_level", level)}
                    className={`w-full h-12 rounded-full border transition-all text-left px-4 ${
                      formData.stress_level === level
                        ? "bg-stone-900 text-white border-stone-900"
                        : "bg-white text-stone-700 border-stone-200 hover:border-stone-400"
                    }`}
                  >
                    {t(
                      `onboarding.step3.stress${
                        level.charAt(0).toUpperCase() + level.slice(1)
                      }`
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-3">
                {t("onboarding.step3.intimacy")}
              </label>
              <div className="space-y-2">
                {["low", "medium", "high"].map((level) => (
                  <button
                    key={level}
                    data-testid={`onboarding-intimacy-${level}-btn`}
                    onClick={() => updateFormData("intimacy_level", level)}
                    className={`w-full h-12 rounded-full border transition-all text-left px-4 ${
                      formData.intimacy_level === level
                        ? "bg-stone-900 text-white border-stone-900"
                        : "bg-white text-stone-700 border-stone-200 hover:border-stone-400"
                    }`}
                  >
                    {t(
                      `onboarding.step3.intimacy${
                        level.charAt(0).toUpperCase() + level.slice(1)
                      }`
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">
                {t("onboarding.step4routine.workSchedule")}
              </label>
              <select
                data-testid="onboarding-work-schedule-select"
                value={formData.work_schedule}
                onChange={(e) => updateFormData("work_schedule", e.target.value)}
                className="w-full h-12 bg-white border border-stone-200 rounded-full px-4 text-stone-900 focus:border-stone-900 focus:ring-0"
              >
                <option value="" disabled>
                  {t("onboarding.step4routine.selectOption")}
                </option>
                <option value="full_time">
                  {t("onboarding.step4routine.fullTime")}
                </option>
                <option value="part_time">
                  {t("onboarding.step4routine.partTime")}
                </option>
                <option value="freelance">
                  {t("onboarding.step4routine.freelance")}
                </option>
                <option value="remote">
                  {t("onboarding.step4routine.remote")}
                </option>
                <option value="shift_work">
                  {t("onboarding.step4routine.shiftWork")}
                </option>
                <option value="not_working">
                  {t("onboarding.step4routine.notWorking")}
                </option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-3">
                {t("onboarding.step4routine.timeTogether")}
              </label>
              <div className="grid grid-cols-3 gap-3">
                {["poco", "medio", "molto"].map((level) => (
                  <button
                    key={level}
                    data-testid={`onboarding-time-${level}-btn`}
                    onClick={() => updateFormData("time_together", level)}
                    className={`h-12 rounded-full border transition-all ${
                      formData.time_together === level
                        ? "bg-stone-900 text-white border-stone-900"
                        : "bg-white text-stone-700 border-stone-200 hover:border-stone-400"
                    }`}
                  >
                    {t(
                      `onboarding.step4routine.time${
                        level.charAt(0).toUpperCase() + level.slice(1)
                      }`
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-3">
                {t("onboarding.step4routine.householdManagement")}
              </label>
              <div className="space-y-2">
                {["mainly_me", "mainly_partner", "balanced"].map((option) => (
                  <button
                    key={option}
                    data-testid={`onboarding-household-${option}-btn`}
                    onClick={() =>
                      updateFormData("household_management", option)
                    }
                    className={`w-full h-12 rounded-full border transition-all text-left px-4 ${
                      formData.household_management === option
                        ? "bg-stone-900 text-white border-stone-900"
                        : "bg-white text-stone-700 border-stone-200 hover:border-stone-400"
                    }`}
                  >
                    {t(
                      `onboarding.step4routine.household${option
                        .split("_")
                        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                        .join("")}`
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case 4:
        const toggleTensionPoint = (point) => {
          const current = formData.tension_points;
          if (current.includes(point)) {
            updateFormData(
              "tension_points",
              current.filter((p) => p !== point)
            );
          } else {
            updateFormData("tension_points", [...current, point]);
          }
        };

        const tensionOptions = [
          "communication",
          "time_together",
          "home_organization",
          "children",
          "intimacy",
          "work_stress",
        ];

        return (
          <div className="space-y-6">
            <p className="text-sm text-stone-500">
              {t("onboarding.step5tension.subtitle")}
            </p>
            <div className="space-y-3">
              {tensionOptions.map((option) => (
                <button
                  key={option}
                  data-testid={`onboarding-tension-${option}-btn`}
                  onClick={() => toggleTensionPoint(option)}
                  className={`w-full h-14 rounded-full border transition-all flex items-center justify-between px-4 ${
                    formData.tension_points.includes(option)
                      ? "bg-stone-900 text-white border-stone-900"
                      : "bg-white text-stone-700 border-stone-200 hover:border-stone-400"
                  }`}
                >
                  <span>{t(`onboarding.step5tension.${option}`)}</span>
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      formData.tension_points.includes(option)
                        ? "border-white bg-white"
                        : "border-stone-300"
                    }`}
                  >
                    {formData.tension_points.includes(option) && (
                      <div className="w-2.5 h-2.5 rounded-full bg-stone-900" />
                    )}
                  </div>
                </button>
              ))}
            </div>
            <p className="text-xs text-stone-400 text-center mt-4">
              {t("onboarding.step5tension.helpText")}
            </p>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-3">
                {t("onboarding.step4.tone")}
              </label>
              <div className="space-y-2">
                {["gentle", "direct", "ironic"].map((tone) => (
                  <button
                    key={tone}
                    data-testid={`onboarding-tone-${tone}-btn`}
                    onClick={() => updateFormData("tone_preference", tone)}
                    className={`w-full h-12 rounded-full border transition-all text-left px-4 ${
                      formData.tone_preference === tone
                        ? "bg-stone-900 text-white border-stone-900"
                        : "bg-white text-stone-700 border-stone-200 hover:border-stone-400"
                    }`}
                  >
                    {t(`onboarding.step4.${tone}`)}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">
                {t("onboarding.step4.reminderTime")}
              </label>
              <input
                data-testid="onboarding-reminder-time-input"
                type="time"
                value={formData.reminder_time}
                onChange={(e) => updateFormData("reminder_time", e.target.value)}
                className="w-full h-12 bg-white border border-stone-200 rounded-full px-4 text-stone-900 focus:border-stone-900 focus:ring-0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">
                {t("onboarding.step4.availability")}
              </label>
              <input
                data-testid="onboarding-availability-input"
                type="number"
                value={formData.daily_availability_minutes}
                onChange={(e) =>
                  updateFormData(
                    "daily_availability_minutes",
                    parseInt(e.target.value, 10) || 10
                  )
                }
                className="w-full h-12 bg-white border border-stone-200 rounded-full px-4 text-stone-900 focus:border-stone-900 focus:ring-0"
              />
              <p className="text-xs text-stone-500 mt-1">
                {t("onboarding.step4.minutes")}
              </p>
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            <div className="text-center mb-4">
              <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-stone-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                  />
                </svg>
              </div>
              <h2 className="text-lg font-medium text-stone-900 mb-2">
                {t("onboarding.step6partner.title")}
              </h2>
              <p className="text-sm text-stone-500">
                {t("onboarding.step6partner.subtitle")}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">
                {t("onboarding.step6partner.partnerName")}
              </label>
              <input
                data-testid="onboarding-partner-name-input"
                type="text"
                value={formData.partner_name}
                onChange={(e) => updateFormData("partner_name", e.target.value)}
                placeholder={t("onboarding.step6partner.partnerNamePlaceholder")}
                className="w-full h-12 bg-white border border-stone-200 rounded-full px-4 text-stone-900 placeholder:text-stone-400 focus:border-stone-900 focus:ring-0"
              />
            </div>
          </div>
        );

      case 7:
        return (
          <div className="space-y-6">
            <div className="text-center mb-4">
              <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-stone-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                  />
                </svg>
              </div>
              <h2 className="text-lg font-medium text-stone-900 mb-2">
                {t("onboarding.step6terms.title")}
              </h2>
              <p className="text-sm text-stone-500">
                {t("onboarding.step6terms.subtitle")}
              </p>
            </div>

            <div className="bg-white border border-stone-200 rounded-xl p-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="accept-terms-checkbox"
                  checked={formData.acceptedTerms}
                  onChange={(e) =>
                    updateFormData("acceptedTerms", e.target.checked)
                  }
                  className="mt-1 w-5 h-5 rounded border-stone-300 text-stone-900 focus:ring-stone-500"
                />
                <div className="flex-1">
                  <span className="text-sm text-stone-700">
                    {t("onboarding.step6terms.acceptTerms")}{" "}
                    <button
                      type="button"
                      onClick={() => window.open("/terms", "_blank")}
                      className="text-stone-900 underline font-medium hover:text-stone-700"
                    >
                      {t("onboarding.step6terms.termsLink")}
                    </button>
                  </span>
                </div>
              </label>
            </div>

            <div className="bg-white border border-stone-200 rounded-xl p-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="accept-privacy-checkbox"
                  checked={formData.acceptedPrivacy}
                  onChange={(e) =>
                    updateFormData("acceptedPrivacy", e.target.checked)
                  }
                  className="mt-1 w-5 h-5 rounded border-stone-300 text-stone-900 focus:ring-stone-500"
                />
                <div className="flex-1">
                  <span className="text-sm text-stone-700">
                    {t("onboarding.step6terms.acceptPrivacy")}{" "}
                    <button
                      type="button"
                      onClick={() => window.open("/privacy", "_blank")}
                      className="text-stone-900 underline font-medium hover:text-stone-700"
                    >
                      {t("onboarding.step6terms.privacyLink")}
                    </button>
                  </span>
                </div>
              </label>
            </div>

            <p className="text-xs text-stone-400 text-center">
              {t("onboarding.step6terms.disclaimer")}
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  /* ============================
     Render
     ============================ */
  return (
    <div className="bg-[#FAFAF9] flex flex-col pb-safe">
      {/* Header */}
      <div className="p-6 max-w-md mx-auto w-full">
        <div className="flex items-center justify-between mb-6">
          {currentStep > 0 && (
            <button
              data-testid="onboarding-back-btn"
              onClick={handleBack}
              className="h-10 px-4 text-stone-500 hover:text-stone-900 hover:bg-stone-100 rounded-full transition-all flex items-center gap-2"
            >
              <ChevronLeft className="w-4 h-4" />
              {t("onboarding.back")}
            </button>
          )}
          <div className="ml-auto text-sm text-stone-500">
            {currentStep + 1} / {totalSteps}
          </div>
        </div>

        <div>
          <h1 className="font-serif text-3xl font-normal tracking-tight text-stone-800 mb-2">
            {t("onboarding.title")}
          </h1>
          <p className="text-sm text-stone-500">{t("onboarding.subtitle")}</p>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 p-6 max-w-md mx-auto w-full overflow-y-auto">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
          >
            {renderStep()}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Footer */}
      <div className="p-6 max-w-md mx-auto w-full">
        <button
          data-testid="onboarding-next-btn"
          onClick={handleNext}
          disabled={!isStepValid() || saving}
          className="w-full h-12 px-8 rounded-full bg-stone-900 text-white font-sans font-medium hover:bg-stone-800 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving
            ? t("common.saving") || "Salvataggio…"
            : currentStep === totalSteps - 1
              ? t("onboarding.finish")
              : t("onboarding.next")}
        </button>
      </div>
    </div>
  );
};

export default Onboarding;
